import React, { ReactElement, useEffect, useState } from 'react';
import moment from 'moment';
import { Checkbox, CheckboxVisibility, ConstrainMode, IColumn, Link, MessageBar, MessageBarType, ShimmeredDetailsList } from '@fluentui/react';
import { fetchPDPatients } from '../../api/providers';
import { PDPatient } from '../../api/providers.types';
import { ROUTES } from '../../config/routes';

interface Props {
  providerId: string;
}

export const ProviderPatients = ({ providerId }: Props): ReactElement => {
  const [patients, setPatients] = useState<PDPatient[]>([]);
  const [fetching, setFetching] = useState(false);
  useEffect(() => {
    setFetching(true);
    fetchPDPatients(providerId)
      .then(setPatients)
      .finally(() => setFetching(false));
  }, [providerId]);

  const columns: IColumn[] = [
    {
      key: 'chartId',
      name: 'Chart ID',
      fieldName: 'chartId',
      minWidth: 100,
      maxWidth: 120,
    },
    {
      key: 'mainPatient',
      name: 'Main Patient',
      minWidth: 80,
      maxWidth: 120,
      onRender: (item: PDPatient) => {
        return <Checkbox disabled={true} checked={item.mainPatientOfAccount} />;
      },
    },
    {
      key: 'patientName',
      name: 'Patient Name',
      minWidth: 120,
      maxWidth: 200,
      isResizable: true,
      onRender: (item: PDPatient) => {
        return (
          <Link href={`${ROUTES.patients}/${item.id}`} target="_blank">
            {item.fullName}
          </Link>
        );
      },
    },
    {
      key: 'accountName',
      name: 'Account Name',
      minWidth: 140,
      maxWidth: 200,
      isResizable: true,
      onRender: (item: PDPatient) => {
        return (
          <Link href={`${ROUTES.accounts}/${item.ownerAccountId}`} target="_blank">
            {item.ownerAccountName}
          </Link>
        );
      },
    },
    {
      key: 'phoneNumber',
      name: 'Patient Phone',
      fieldName: 'phoneNumber',
      minWidth: 80,
      maxWidth: 140,
    },
    {
      key: 'isHubActive',
      name: 'Heal Hub',
      minWidth: 80,
      maxWidth: 120,
      onRender: (item: PDPatient) => {
        return <Checkbox disabled={true} checked={item.isHubActive} />;
      },
    },
    {
      key: 'since',
      name: 'Since',
      minWidth: 80,
      maxWidth: 120,
      onRender: (item: PDPatient) => {
        return moment(item.preferredDoctor.since).format('MM/DD/YYYY');
      },
    },
    {
      key: 'attributionStatus',
      name: 'Status',
      minWidth: 80,
      maxWidth: 200,
      onRender: (item: PDPatient) => {
        return item.preferredDoctor.status;
      },
    },
    {
      key: 'stillValid',
      name: 'Valid',
      minWidth: 80,
      maxWidth: 120,
      onRender: (item: PDPatient) => {
        return <Checkbox disabled={true} checked={item.preferredDoctor.stillValid} />;
      },
    },
  ];

  return (
    <>
      <ShimmeredDetailsList
        enableShimmer={fetching}
        selectionPreservedOnEmptyClick={true}
        checkboxVisibility={CheckboxVisibility.hidden}
        constrainMode={ConstrainMode.horizontalConstrained}
        items={patients}
        setKey="set"
        columns={columns}
        onShouldVirtualize={() => false}
      />
      {!fetching && patients.length === 0 && (
        <MessageBar messageBarType={MessageBarType.info} isMultiline={true}>
          This provider does not have any PD patients at this time.
        </MessageBar>
      )}
    </>
  );
};
